import { useEffect, useState } from 'react';
import './chatBotFaqModal.css';
import {
    supGetChatBotFaq,
    supCreateChatBotFaq,
    supUpdateChatBotFaq,
    supDeleteChatBotFaq,
    supTestChatBotFaq
} from '../../../api/supportAdmin';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import ConfirmationModal from '../../../components/tripStrap/tripStrapFun/cancelBooking';
import CloseIcon from '../../../assets/icons/trvto_close.svg';

const LIMIT = 20;
const emptyForm = { question: '', answer: '', keywords: '', category: 'General', isActive: true };

const ChatBotFaqModal = ({ open, onClose }) => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [loading, setLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    // live test
    const [testMsg, setTestMsg] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await supGetChatBotFaq({ search: debounced, page, limit: LIMIT });
            setData(res.data.data || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to load Q&A.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchData();
        // eslint-disable-next-line
    }, [open, debounced, page]);

    const openAdd = () => {
        setEditData(null);
        setForm(emptyForm);
        setShowForm(true);
    };

    const openEdit = (item) => {
        setEditData(item);
        setForm({
            question: item.question || '',
            answer: item.answer || '',
            keywords: (item.keywords || []).join(', '),
            category: item.category || 'General',
            isActive: item.isActive !== false
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditData(null);
        setForm(emptyForm);
    };

    const submit = async () => {
        if (!form.question.trim()) return toast.warning('Enter a question.');
        if (!form.answer.trim()) return toast.warning('Enter an answer.');
        const body = {
            question: form.question.trim(),
            answer: form.answer.trim(),
            keywords: form.keywords,
            category: form.category.trim() || 'General',
            isActive: form.isActive
        };
        try {
            setSaving(true);
            const res = editData
                ? await supUpdateChatBotFaq(editData._id, body)
                : await supCreateChatBotFaq(body);
            toast.success(res?.data?.message || 'Saved.');
            closeForm();
            fetchData();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (item) => {
        try {
            await supUpdateChatBotFaq(item._id, { isActive: !item.isActive });
            setData((d) => d.map((x) => (x._id === item._id ? { ...x, isActive: !item.isActive } : x)));
        } catch (err) {
            toast.error('Failed to update.');
        }
    };

    const doDelete = async () => {
        try {
            const res = await supDeleteChatBotFaq(deleteItem._id);
            toast.success(res?.data?.message || 'Deleted.');
            if (editData?._id === deleteItem._id) closeForm();
            setDeleteItem(null);
            fetchData();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to delete.');
            setDeleteItem(null);
        }
    };

    const runTest = async () => {
        if (!testMsg.trim()) return;
        try {
            setTesting(true);
            setTestResult(null);
            const res = await supTestChatBotFaq(testMsg.trim());
            setTestResult(res.data);
        } catch (err) {
            toast.error('Test failed.');
        } finally {
            setTesting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    if (!open) return null;
    return (
        <div className='faq-overlay'>
            <div className='faq-modal'>
                <div className='faq-header'>
                    <div>
                        <h2>Chatbot Q&amp;A · Auto-replies</h2>
                        <p>When a lead asks something that matches a question here, the bot replies with your answer automatically.</p>
                    </div>
                    <img src={CloseIcon} alt='close' width={30} height={30} onClick={onClose} />
                </div>

                {/* live test */}
                <div className='faq-test'>
                    <div className='faq-test-input'>
                        <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
                            <circle cx='11' cy='11' r='7' />
                            <path d='M21 21l-4.3-4.3' />
                        </svg>
                        <input
                            placeholder='Type a lead message to test which answer fires…'
                            value={testMsg}
                            onChange={(e) => setTestMsg(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && runTest()}
                        />
                        <button className='faq-test-btn' onClick={runTest} disabled={testing}>
                            {testing ? 'Testing…' : 'Test'}
                        </button>
                    </div>
                    {testResult && (
                        <div className={`faq-test-result ${testResult.matched ? 'hit' : 'miss'}`}>
                            {testResult.matched ? (
                                <>
                                    <span className='faq-test-badge'>Match · {testResult.confidence}%</span>
                                    <div className='faq-test-q'>Q: {testResult.entry?.question}</div>
                                    <div className='faq-test-a'>{testResult.entry?.answer}</div>
                                </>
                            ) : (
                                <span className='faq-test-nomatch'>No answer matches — the bot would fall back to its default reply.</span>
                            )}
                        </div>
                    )}
                </div>

                <div className='faq-body'>
                    <div className='faq-list-section'>
                        <div className='faq-toolbar'>
                            <input
                                className='faq-search'
                                placeholder='Search questions, answers, keywords…'
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                            />
                            <button className='faq-add' onClick={openAdd}>
                                + Add Q&amp;A
                            </button>
                        </div>

                        {loading ? (
                            <div className='faq-loading'>
                                <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                            </div>
                        ) : data.length === 0 ? (
                            <div className='faq-empty'>No Q&amp;A yet. Add your first auto-reply.</div>
                        ) : (
                            <div className='faq-table'>
                                <div className='faq-row faq-row-head'>
                                    <span>Question</span>
                                    <span>Answer</span>
                                    <span>Keywords</span>
                                    <span>Matched</span>
                                    <span>Active</span>
                                    <span></span>
                                </div>
                                {data.map((item) => (
                                    <div className='faq-row' key={item._id} onClick={() => openEdit(item)}>
                                        <span className='faq-q'>
                                            <b>{item.question}</b>
                                            <em className='faq-cat'>{item.category || 'General'}</em>
                                        </span>
                                        <span className='faq-a'>{item.answer}</span>
                                        <span className='faq-kw'>
                                            {(item.keywords || []).slice(0, 3).map((k) => (
                                                <em key={k}>{k}</em>
                                            ))}
                                            {(item.keywords || []).length > 3 && <em>+{item.keywords.length - 3}</em>}
                                        </span>
                                        <span className='faq-hits'>{item.hitCount || 0}</span>
                                        <span onClick={(e) => e.stopPropagation()}>
                                            <button
                                                className={`faq-toggle ${item.isActive ? 'on' : ''}`}
                                                onClick={() => toggleActive(item)}
                                                title={item.isActive ? 'Active' : 'Off'}
                                            >
                                                <span className='faq-knob' />
                                            </button>
                                        </span>
                                        <span className='faq-actions' onClick={(e) => e.stopPropagation()}>
                                            <button className='faq-del' onClick={() => setDeleteItem(item)} title='Delete'>
                                                🗑
                                            </button>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className='faq-pagination'>
                                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                                    Prev
                                </button>
                                <span>
                                    {page} / {totalPages}
                                </span>
                                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                                    Next
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={`faq-form-section ${showForm ? 'open' : ''}`}>
                        {showForm && (
                            <div className='faq-form'>
                                <div className='faq-form-head'>
                                    <h3>{editData ? 'Edit Q&A' : 'Add Q&A'}</h3>
                                    <button className='faq-form-close' onClick={closeForm}>
                                        ✕
                                    </button>
                                </div>

                                <label>Question</label>
                                <input
                                    value={form.question}
                                    onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                                    placeholder='e.g. What is the cancellation policy?'
                                />

                                <label>Answer (sent to the lead)</label>
                                <textarea
                                    rows={5}
                                    value={form.answer}
                                    onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                                    placeholder='e.g. Free cancellation up to 7 days before the trip.'
                                />

                                <label>
                                    Keywords <span>(optional, comma-separated — extra words that should trigger this)</span>
                                </label>
                                <input
                                    value={form.keywords}
                                    onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                                    placeholder='refund, cancel, money back'
                                />

                                <div className='faq-form-row'>
                                    <div className='faq-form-col'>
                                        <label>Category</label>
                                        <input
                                            value={form.category}
                                            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                                            placeholder='General'
                                        />
                                    </div>
                                    <div className='faq-form-col faq-active-col'>
                                        <label>Active</label>
                                        <button
                                            type='button'
                                            className={`faq-toggle ${form.isActive ? 'on' : ''}`}
                                            onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                                        >
                                            <span className='faq-knob' />
                                        </button>
                                    </div>
                                </div>

                                <div className='faq-form-foot'>
                                    <button className='faq-ghost' onClick={closeForm}>
                                        Cancel
                                    </button>
                                    <button className='faq-save' onClick={submit} disabled={saving}>
                                        {saving ? 'Saving…' : editData ? 'Update' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {deleteItem && (
                    <ConfirmationModal
                        setShowModal={setDeleteItem}
                        handleConfirm={doDelete}
                        message={'Delete this Q&A?'}
                    />
                )}
            </div>
        </div>
    );
};

export default ChatBotFaqModal;
